import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
  noIndex?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const removeMetaTag = (name: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      const element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (element) element.remove();
    };

    // Robots / noindex
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      removeMetaTag('robots');
    }

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Canonical URL
    const fullCanonical = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${SITE_URL}${canonical}`
      : SITE_URL;

    let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.rel = 'canonical';
      document.head.appendChild(linkElement);
    }
    linkElement.href = fullCanonical;

    // Open Graph
    const fullOgImage = ogImage || `${SITE_URL}/og-image.jpg`;
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', fullCanonical, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', fullOgImage, true);

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', fullCanonical);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', fullOgImage);

    // Schema.org JSON-LD
    if (schema) {
      // Remove any existing JSON-LD scripts injected by this hook
      document
        .querySelectorAll('script[type="application/ld+json"][data-seo-hook]')
        .forEach((el) => el.remove());

      const scriptElement = document.createElement('script');
      scriptElement.type = 'application/ld+json';
      scriptElement.setAttribute('data-seo-hook', 'true');
      scriptElement.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptElement);
    }
  }, [title, description, keywords, canonical, ogImage, schema, noIndex]);
}

// ─── Schema generators ────────────────────────────────────────────────────────

export const generateWebPageSchema = (
  name: string,
  description: string,
  urlPath: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: `${SITE_URL}${urlPath}`,
  inLanguage: 'de-DE',
  publisher: {
    '@type': 'Organization',
    name: 'BR Webdesign & AI Solutions',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
    },
  },
});

export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'BR Webdesign & AI Solutions',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    'Professionelles Webdesign, AI Chatbots und SEO-Optimierung von Benjamin Reick in Titisee-Neustadt',
  founder: {
    '@type': 'Person',
    name: 'Benjamin Reick',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Freiburger Straße 19',
    addressLocality: 'Titisee-Neustadt',
    postalCode: '79822',
    addressRegion: 'Baden-Württemberg',
    addressCountry: 'DE',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '47.9133',
    longitude: '8.2067',
  },
  areaServed: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: '47.9133',
      longitude: '8.2067',
    },
    geoRadius: '50000',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+49-162-3651571',
    contactType: 'customer service',
    email: 'info-aiagents@web.de',
    availableLanguage: 'de',
    areaServed: 'DE',
  },
  sameAs: [],
});

export const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  name: 'BR Webdesign & AI Solutions',
  image: [
    `${SITE_URL}/logo.png`,
    `${SITE_URL}/og-image.jpg`,
  ],
  '@id': `${SITE_URL}/#localbusiness`,
  url: SITE_URL,
  telephone: '+49-162-3651571',
  email: 'info-aiagents@web.de',
  priceRange: '€€',
  currenciesAccepted: 'EUR',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer',
  description:
    'Professionelles Webdesign, AI Chatbots und SEO-Optimierung für Unternehmen in Titisee-Neustadt und dem Schwarzwald. Benjamin Reick verwandelt Webseiten in digitale Verkaufsmaschinen.',
  founder: {
    '@type': 'Person',
    name: 'Benjamin Reick',
    jobTitle: 'Webdesigner & AI-Spezialist',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Freiburger Straße 19',
    addressLocality: 'Titisee-Neustadt',
    postalCode: '79822',
    addressRegion: 'Baden-Württemberg',
    addressCountry: 'DE',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 47.9133,
    longitude: 8.2067,
  },
  hasMap: 'https://maps.google.com/?q=Freiburger+Straße+19,+79822+Titisee-Neustadt',
  areaServed: [
    { '@type': 'City', name: 'Titisee-Neustadt' },
    { '@type': 'City', name: 'Freiburg im Breisgau' },
    { '@type': 'State', name: 'Baden-Württemberg' },
    { '@type': 'Country', name: 'Deutschland' },
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Webdesign & AI Leistungen',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Webseite Komplett',
          description:
            'Professionelle Webseite mit bis zu 7 Seiten, Responsive Design, SEO-Optimierung, Kontaktformular, SSL-Zertifikat und DSGVO-Absicherung',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'AI Chatbot Integration',
          description:
            'Intelligente AI-Chatbots für automatisierten Kundensupport und Lead-Generierung rund um die Uhr',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'SEO & Sichtbarkeit',
          description:
            'Suchmaschinenoptimierung für bessere Google-Rankings und mehr organischen Traffic',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Premium Redesign',
          description:
            'Komplette Überarbeitung bestehender Webseiten für modernes Design und bessere Performance',
        },
      },
    ],
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '12',
    bestRating: '5',
    worstRating: '1',
  },
  sameAs: [],
});

export const generateServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Webdesign & AI Solutions',
  provider: {
    '@type': 'Organization',
    name: 'BR Webdesign & AI Solutions',
    url: SITE_URL,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Titisee-Neustadt',
      postalCode: '79822',
      addressRegion: 'Baden-Württemberg',
      addressCountry: 'DE',
    },
  },
  areaServed: [
    { '@type': 'City', name: 'Titisee-Neustadt' },
    { '@type': 'State', name: 'Baden-Württemberg' },
    { '@type': 'Country', name: 'Deutschland' },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Webdesign Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Webseite Komplett',
          description:
            'Professionelle Webseite mit bis zu 7 Seiten, Responsive Design, SEO-Optimierung, Kontaktformular, SSL-Zertifikat und DSGVO-Absicherung',
        },
      },
    ],
  },
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generatePersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Benjamin Reick',
  jobTitle: 'Webdesigner & AI-Spezialist',
  url: `${SITE_URL}/about`,
  telephone: '+49-162-3651571',
  email: 'info-aiagents@web.de',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Titisee-Neustadt',
    postalCode: '79822',
    addressRegion: 'Baden-Württemberg',
    addressCountry: 'DE',
  },
  worksFor: {
    '@type': 'Organization',
    name: 'BR Webdesign & AI Solutions',
  },
});

export const generateBreadcrumbSchema = (
  items: Array<{ name: string; url: string }>
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.url}`,
  })),
});

export const generateContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Kontakt – BR Webdesign & AI Solutions',
  description:
    'Kontaktieren Sie Benjamin Reick für professionelles Webdesign in Titisee-Neustadt.',
  url: `${SITE_URL}/contact`,
  mainEntity: {
    '@type': 'Organization',
    name: 'BR Webdesign & AI Solutions',
    telephone: '+49-162-3651571',
    email: 'info-aiagents@web.de',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Freiburger Straße 19',
      addressLocality: 'Titisee-Neustadt',
      postalCode: '79822',
      addressRegion: 'Baden-Württemberg',
      addressCountry: 'DE',
    },
  },
});
